import { Injectable, NgZone, OnDestroy } from '@angular/core';

import { runInZone } from '@core/operators';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter, take, withLatestFrom } from 'rxjs/operators';

import { BroadcastInstances, BroadcastMessage } from '@core/interfaces';

import { ChangeCustomerResponseBody, ChangeFacilityResponseBody } from '#types/auth';

import { AuthService } from '@auth/services';
import { AuthActions } from '@auth/state';

import { messageType } from './constants';

@Injectable({
  providedIn: 'root',
})
export class BroadcastService implements OnDestroy {
  debug = false;

  id: string = null;

  timeout = 15000; // after this many ms, clients are considered "dead" and removed
  interval = 5000; // interval to ping other clients to update status

  startTime = Date.now();

  channel: BroadcastChannel;
  channelName = 'synapse-anywhere';

  message$ = new Subject<BroadcastMessage>();

  instances = new Map<string, BroadcastInstances>();
  instances$ = new BehaviorSubject<number>(0);

  leader: string = null;
  leader$ = new BehaviorSubject<string>(null);

  environment: string;
  channelName$ = new BehaviorSubject<string>(null);

  constructor(private store: Store, private ngZone: NgZone, private authService: AuthService) {
    if (document.defaultView.location.hostname !== 'localhost') {
      this.environment = document.defaultView.location.pathname.split('/')[1] ?? 'dev';
    } else {
      this.environment = 'dev';
    }

    (window as any).broadcastDebug = (value: boolean) => {
      this.debug = value ?? false;
    };

    // create broadcast channel and listen for events
    this.channel = new BroadcastChannel(`${this.channelName}-${this.environment}`);

    this.channelName$.next(this.channel.name);

    this.log('broadcast channel name:', this.channel.name);

    this.authService.id$.pipe().subscribe((id) => {
      if (this.id !== null && this.id !== id) {
        this.instances.delete(this.id);
      }

      this.id = id;

      // remove any existing listeners
      this.channel.removeAllListeners('message');

      this.channel.addEventListener('message', (event: MessageEvent<BroadcastMessage>) => {
        this.message$.next(event.data);
      });
    });

    // announce to other clients
    this.announce();

    // listen for messages
    this.listen();

    // request updates from other clients
    setInterval(() => {
      this.ping();

      for (const [id, instance] of this.instances) {
        if (Date.now() - instance.lastActive > this.timeout) {
          this.log('removing dead instance:', id);
          this.instances.delete(id);
        }
      }

      this.instances$.next(this.instances.size);

      this.electLeader();
    }, this.interval);
  }

  ngOnDestroy() {
    if (this.channel) {
      this.channel.close();
    }
  }

  electLeader() {
    let earliestStartTime = this.startTime;

    for (const instance of this.instances.values()) {
      if (instance.startTime < earliestStartTime) {
        earliestStartTime = instance.startTime;
      }
    }

    for (const instance of this.instances.values()) {
      if (instance.startTime === earliestStartTime) {
        this.leader = instance.id;
        this.leader$.next(this.leader);
      }
    }

    this.authService.broadcastLeader = this.leader === this.id;
  }

  announce() {
    this.authService.state$.pipe(take(1)).subscribe((state) => {
      this.send({
        type: messageType.announce,
        payload: { authenticated: state.authenticated, url: new URL(window.location.href).pathname },
      });

      this.instances.set(state.id, {
        id: state.id,
        startTime: this.startTime,
        lastActive: Date.now(),
      });
    });

    this.instances$.next(this.instances.size);
  }

  ping() {
    this.authService.state$.pipe(take(1)).subscribe((state) => {
      this.send({ type: messageType.ping, payload: { authenticated: state.authenticated, startTime: this.startTime } });

      this.instances.set(state.id, {
        id: state.id,
        startTime: this.startTime,
        lastActive: Date.now(),
      });
    });

    this.instances$.next(this.instances.size);
  }

  send(message: BroadcastMessage) {
    this.log('send:', message);

    this.channel.postMessage({
      from: this.id,
      to: message.to ?? 'ALL',
      type: message.type,
      payload: message.payload ?? null,
      ts: Date.now(),
    });
  }

  messages$<T = any>(type: string): Observable<BroadcastMessage<T>> {
    return this.message$.pipe(
      runInZone(this.ngZone),
      filter((message) => message.to === 'ALL' || message.to === this.id),
      filter((message) => message.type === type)
    );
  }

  log(...params: any) {
    if (this.debug) {
      console.log(...params);
    }
  }

  listen() {
    this.messages$(messageType.announce).subscribe((message) => {
      this.log('received announce:', message);

      if (!message.payload.authenticated) {
        this.authService.state$.pipe(take(1)).subscribe((state) => {
          if (state.authenticated) {
            this.send({
              to: message.from,
              type: messageType.login,
              payload: {
                authState: {
                  ...state,
                  id: null,
                },
                redirect: message.payload?.url,
              },
            });
          }

          this.instances.set(message.from, {
            id: message.from,
            startTime: message.payload?.startTime,
            lastActive: message.ts,
          });
        });
      }

      this.instances$.next(this.instances.size);
    });

    this.messages$(messageType.ping).subscribe((message) => {
      this.log('received ping:', message);

      this.instances.set(message.from, {
        id: message.from,
        startTime: message.payload?.startTime,
        lastActive: message.ts,
      });

      this.instances$.next(this.instances.size);
    });

    this.messages$(messageType.login)
      .pipe(
        withLatestFrom(this.authService.authenticated$),
        filter(([_, authenticated]) => !authenticated)
      )
      .subscribe(([message]) => {
        this.log('received login:', message);
        this.store.dispatch(AuthActions.LoginFromBroadcast({ payload: message.payload?.authState, redirect: message.payload?.redirect }));
      });

    this.messages$(messageType.logout)
      .pipe(
        withLatestFrom(this.authService.authenticated$),
        filter(([_, authenticated]) => authenticated)
      )
      .subscribe(([message]) => {
        this.log('received logout:', message);
        this.store.dispatch(AuthActions.Logout());
      });

    this.messages$<string>(messageType.theme)
      .pipe(
        withLatestFrom(this.authService.authenticated$),
        filter(([_, authenticated]) => authenticated)
      )
      .subscribe(([message]) => {
        this.log('received theme:', message);
        this.store.dispatch(AuthActions.ChangeThemeFromBroadcast({ theme: message.payload }));
      });

    this.messages$<string>(messageType.language)
      .pipe(
        withLatestFrom(this.authService.authenticated$),
        filter(([_, authenticated]) => authenticated)
      )
      .subscribe(([message]) => {
        this.log('received language:', message);
        this.store.dispatch(AuthActions.ChangeLanguageFromBroadcast({ language: message.payload }));
      });

    this.messages$<ChangeFacilityResponseBody>(messageType.changeFacility)
      .pipe(
        withLatestFrom(this.authService.authenticated$),
        filter(([_, authenticated]) => authenticated)
      )
      .subscribe(([message]) => {
        this.log('received changeFacility:', message);
        this.store.dispatch(AuthActions.ChangeFacilityFromBroadcast({ payload: message.payload }));
      });

    this.messages$<ChangeCustomerResponseBody>(messageType.changeCustomer)
      .pipe(
        withLatestFrom(this.authService.authenticated$),
        filter(([_, authenticated]) => authenticated)
      )
      .subscribe(([message]) => {
        this.log('received changeCustomer:', message);
        this.store.dispatch(AuthActions.ChangeCustomerFromBroadcast({ payload: message.payload }));
      });

    this.messages$<boolean>(messageType.timeout)
      .pipe(
        withLatestFrom(this.authService.authenticated$),
        filter(([_, authenticated]) => authenticated)
      )
      .subscribe(([message]) => {
        this.log('received timeout:', message);
        this.store.dispatch(AuthActions.SetSessionTimeout({ timeout: message.payload }));
      });
  }
}
